import { useEffect, useRef } from "react";
import { Bold, Italic, List, Link as LinkIcon, ListOrdered, Eye, Pencil } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/cn";
import { RichText } from "./RichText";

interface Props {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  rows?: number;
  className?: string;
  textareaClassName?: string;
  autoFocus?: boolean;
  /** Render the textarea as h-full inside a flex container instead of fixed rows. */
  fill?: boolean;
  /** Compact mode for inline / dense contexts. Drops the Preview toggle
   *  and the footer hint, and uses a smaller toolbar. Toolbar still shows
   *  bold/italic/lists so formatting is reachable, just without the
   *  chrome of the full editor. */
  compact?: boolean;
}

/**
 * Lightweight markdown editor: textarea + small toolbar that wraps the
 * selection (or inserts at cursor) with markdown syntax. Intentionally
 * not a WYSIWYG — the stored value is plain markdown so it works with
 * the agent's tool calls, search, and any future plain-text export.
 */
export function RichTextEditor({
  value,
  onChange,
  placeholder,
  rows = 4,
  className,
  textareaClassName,
  autoFocus = false,
  fill = false,
  compact = false,
}: Props) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [mode, setMode] = useState<"write" | "preview">("write");

  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);

  /** Auto-continue the current list when the user hits Enter inside one.
   *  Mirrors the behavior every editor people are used to (Notion, GitHub,
   *  Slack): pressing Enter on a `- foo` line drops a fresh `- ` on the
   *  next line; numbered lists auto-increment. Hitting Enter on an EMPTY
   *  list item breaks out of the list (strips the marker), which is the
   *  standard "I'm done with the list" gesture.
   *
   *  Plain Enter without a list context is left alone — the textarea's
   *  default behavior wins. */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== "Enter" || e.shiftKey || e.altKey || e.metaKey || e.ctrlKey) return;
    const ta = ref.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    // Don't interfere with active selections — replacing them is the
    // textarea default and the user almost certainly wants that.
    if (start !== end) return;

    // Slice the current line — from the last newline (or start of doc) to
    // the cursor. Anything after the cursor on that line gets carried to
    // the next list item, matching how Notion/GitHub behave.
    const before = value.slice(0, start);
    const lineStart = before.lastIndexOf("\n") + 1;
    const currentLine = value.slice(lineStart, start);

    // Match list markers in priority order. Task list (- [ ]) before
    // bullet (- ) so the `[ ]` doesn't get swallowed as content.
    const taskMatch = currentLine.match(/^(\s*)([-*+])\s\[([ xX])\]\s(.*)$/);
    const ulMatch = !taskMatch ? currentLine.match(/^(\s*)([-*+])\s(.*)$/) : null;
    const olMatch =
      !taskMatch && !ulMatch ? currentLine.match(/^(\s*)(\d+)\.\s(.*)$/) : null;

    let nextMarker: string | null = null;
    let content = "";
    if (taskMatch) {
      nextMarker = `${taskMatch[1]}${taskMatch[2]} [ ] `;
      content = taskMatch[4];
    } else if (ulMatch) {
      nextMarker = `${ulMatch[1]}${ulMatch[2]} `;
      content = ulMatch[3];
    } else if (olMatch) {
      const nextNum = parseInt(olMatch[2], 10) + 1;
      nextMarker = `${olMatch[1]}${nextNum}. `;
      content = olMatch[3];
    }

    if (nextMarker === null) return; // not in a list — let default Enter run

    e.preventDefault();

    // Empty item → exit the list. Drop the marker from the current line
    // (replacing it with an empty line) and place the cursor at the start
    // of that empty line. The `\n` we'd otherwise insert is implicit in
    // the wipe below.
    if (content.trim() === "") {
      const next = value.slice(0, lineStart) + "\n" + value.slice(end);
      onChange(next);
      requestAnimationFrame(() => {
        ta.focus();
        const pos = lineStart + 1;
        ta.setSelectionRange(pos, pos);
      });
      return;
    }

    // Continue the list — newline + same-shape marker, cursor past it.
    const insertion = `\n${nextMarker}`;
    const next = value.slice(0, start) + insertion + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + insertion.length;
      ta.setSelectionRange(pos, pos);
    });
  };

  const apply = (kind: "bold" | "italic" | "ul" | "ol" | "link") => {
    const ta = ref.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = value.slice(start, end);
    const hasSelection = start !== end;

    // Inline wraps (bold, italic, link). When nothing is selected, insert
    // empty markers and drop the cursor between them so the user types
    // their own text — never persist a "bold text" / "item" placeholder.
    if (kind === "bold" || kind === "italic" || kind === "link") {
      const before = kind === "bold" ? "**" : kind === "italic" ? "*" : "[";
      const after = kind === "bold" ? "**" : kind === "italic" ? "*" : "](url)";
      const replacement = `${before}${selected}${after}`;
      const next = value.slice(0, start) + replacement + value.slice(end);
      onChange(next);
      requestAnimationFrame(() => {
        ta.focus();
        // Cursor between markers when no selection; after the replacement
        // when there was one (so the user keeps typing past it).
        const pos = hasSelection ? start + replacement.length : start + before.length;
        ta.setSelectionRange(pos, pos);
      });
      return;
    }

    // Lists: ensure each item lives on its own line and the list sits on
    // a fresh line so marked parses it as a real <ul>/<ol> instead of
    // glueing "- " into the prior paragraph.
    const prefixFor = (i: number) => (kind === "ul" ? "- " : `${i + 1}. `);
    const lines = hasSelection ? selected.split("\n") : [""];
    const formatted = lines.map((l, i) => `${prefixFor(i)}${l}`).join("\n");

    // Need a blank line before the list unless we're at the start of the
    // doc or already on an empty line.
    const charBefore = start > 0 ? value[start - 1] : "";
    const twoBefore = start > 1 ? value.slice(start - 2, start) : "";
    let leading = "";
    if (start > 0 && charBefore !== "\n") leading = "\n\n";
    else if (start > 1 && twoBefore !== "\n\n" && charBefore === "\n") leading = "\n";

    const replacement = `${leading}${formatted}`;
    const next = value.slice(0, start) + replacement + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      // Drop cursor at end of the first marker so the user types the item.
      const pos = hasSelection
        ? start + replacement.length
        : start + leading.length + prefixFor(0).length;
      ta.setSelectionRange(pos, pos);
    });
  };

  return (
    <div className={cn("flex flex-col gap-1.5", fill && "h-full", className)}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-0.5 rounded-md border border-line bg-surface p-0.5">
          <ToolbarButton onClick={() => apply("bold")} title="Bold" icon={Bold} />
          <ToolbarButton onClick={() => apply("italic")} title="Italic" icon={Italic} />
          <span className="mx-0.5 h-4 w-px bg-muted" />
          <ToolbarButton onClick={() => apply("ul")} title="Bulleted list" icon={List} />
          <ToolbarButton onClick={() => apply("ol")} title="Numbered list" icon={ListOrdered} />
          <ToolbarButton onClick={() => apply("link")} title="Link" icon={LinkIcon} />
        </div>
        {!compact && (
          <button
            type="button"
            onClick={() => setMode((m) => (m === "write" ? "preview" : "write"))}
            className="flex items-center gap-1 rounded-md border border-line bg-surface px-2 py-1 text-[11px] font-medium text-ink-3 hover:bg-raised transition-colors"
            title={mode === "write" ? "Preview" : "Edit"}
          >
            {mode === "write" ? <Eye className="h-3 w-3" /> : <Pencil className="h-3 w-3" />}
            {mode === "write" ? "Preview" : "Edit"}
          </button>
        )}
      </div>

      {mode === "write" ? (
        <textarea
          ref={ref}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={fill ? undefined : rows}
          placeholder={placeholder}
          className={cn(
            "w-full rounded-lg border border-line-strong bg-surface px-3 py-2 font-mono text-[13px] leading-relaxed text-ink placeholder:text-ink-subtle focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-400",
            fill ? "flex-1 resize-none" : "resize-y",
            textareaClassName,
          )}
        />
      ) : (
        <div
          className={cn(
            "w-full overflow-y-auto rounded-lg border border-line bg-canvas/40 px-3 py-2",
            fill ? "flex-1" : "min-h-[6rem]",
          )}
        >
          {value.trim() ? (
            <RichText text={value} />
          ) : (
            <p className="text-sm italic text-ink-subtle">Nothing to preview yet.</p>
          )}
        </div>
      )}

      {!compact && (
        <p className="text-[10px] text-ink-subtle">
          Markdown supported — **bold**, *italic*, - bullets, [text](url).
        </p>
      )}
    </div>
  );
}

function ToolbarButton({
  onClick,
  title,
  icon: Icon,
}: {
  onClick: () => void;
  title: string;
  icon: typeof Bold;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="rounded p-1 text-ink-muted hover:bg-raised hover:text-ink-2 transition-colors"
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}
